import React, { useEffect, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { openCalendlyPopup } from '../utils/calendly';
import { CheckCircleIcon, ArrowRightIcon, XCircleIcon, SparklesIcon, ExternalLinkIcon, CalendarIcon } from 'lucide-react';
export function KraftCrmPage() {
  const {
    language
  } = useLanguage();
  // Add comprehensive Schema markup for SEO
  useEffect(() => {
    // Organization Schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'CRM Kraft',
      alternateName: 'Kraft CRM',
      url: 'https://kraftcrm.de',
      logo: 'https://kraftcrm.de/logo.png',
      description: language === 'de' ? 'CRM Kraft ist ein spezialisierter CRM-Beratungs- und Implementierungsservice. Offizieller Close CRM Partner.' : 'CRM Kraft is a specialized CRM consulting and implementation service. Official Close CRM Partner.',
      sameAs: ['https://www.instagram.com/kraftjustus/', 'https://www.facebook.com/kraftjustus', 'https://www.linkedin.com/in/justus-kraft/'],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Sales',
        availableLanguage: ['German', 'English']
      }
    };
    // FAQ Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: language === 'de' ? 'Heißt es CRM Kraft oder CRM Craft?' : 'Is it CRM Kraft or CRM Craft?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Der korrekte Name ist CRM Kraft. Wir heißen CRM Kraft, weil wir die Kraft (Power) in dein CRM-System bringen. Viele suchen nach "CRM Craft", aber wir sind CRM Kraft - dein Partner für leistungsstarke CRM-Lösungen.' : 'The correct name is CRM Kraft. We are called CRM Kraft because we bring the power (Kraft) into your CRM system. Many search for "CRM Craft", but we are CRM Kraft - your partner for powerful CRM solutions.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Ist CRM Kraft ein eigenes CRM-System?' : 'Is CRM Kraft its own CRM system?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Nein, CRM Kraft ist kein eigenes CRM-System. Wir sind ein spezialisierter CRM-Beratungs- und Implementierungsservice. Wir arbeiten hauptsächlich mit Close CRM und helfen Unternehmen, ihre CRM-Systeme optimal zu nutzen.' : 'No, CRM Kraft is not its own CRM system. We are a specialized CRM consulting and implementation service. We mainly work with Close CRM and help companies optimally use their CRM systems.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Mit welchen CRM-Systemen arbeitet CRM Kraft?' : 'Which CRM systems does CRM Kraft work with?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'CRM Kraft ist offizieller Close Partner und spezialisiert auf Close CRM. Wir haben über 110 CRM-Systeme erfolgreich implementiert und arbeiten mit über 1.200 Vertriebsprofis zusammen.' : 'CRM Kraft is an official Close CRM Partner and specializes in Close CRM. We have successfully implemented over 110 CRM systems and work with over 1,200 sales professionals.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Welche Leistungen bietet CRM Kraft an?' : 'What services does CRM Kraft offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'CRM Kraft bietet CRM-Strategie & Beratung, Close CRM Implementierung, KI-gestützte Automatisierung, Vertriebsprozess-Optimierung, Team Training & Support sowie laufende Optimierung.' : 'CRM Kraft offers CRM Strategy & Consulting, Close CRM Implementation, AI-powered Automation, Sales Process Optimization, Team Training & Support, and Ongoing Optimization.'
        }
      }]
    };
    // Service Schema
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'CRM Consulting and Implementation',
      provider: {
        '@type': 'Organization',
        name: 'CRM Kraft'
      },
      areaServed: {
        '@type': 'Country',
        name: 'Germany'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'CRM Services',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'de' ? 'CRM-Strategie & Beratung' : 'CRM Strategy & Consulting'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'de' ? 'Close CRM Implementierung' : 'Close CRM Implementation'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'de' ? 'KI-gestützte Automatisierung' : 'AI-powered Automation'
          }
        }]
      }
    };
    // Insert schemas
    const insertSchema = (schema: object, id: string) => {
      let scriptTag = document.querySelector(`script[data-schema="${id}"]`);
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('data-schema', id);
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    };
    insertSchema(organizationSchema, 'organization');
    insertSchema(faqSchema, 'faq');
    insertSchema(serviceSchema, 'service');
    // Add preconnect for performance
    const addPreconnect = (href: string) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        document.head.appendChild(link);
      }
    };
    addPreconnect('https://assets.calendly.com');
    addPreconnect('https://calendly.com');
    return () => {
      // Cleanup schemas
      document.querySelector('script[data-schema="organization"]')?.remove();
      document.querySelector('script[data-schema="faq"]')?.remove();
      document.querySelector('script[data-schema="service"]')?.remove();
    };
  }, [language]);
  const content = {
    de: {
      hero: {
        title: 'CRM Kraft oder CRM Craft?',
        subtitle: 'Die Antwort: Es heißt CRM Kraft',
        description: 'Viele suchen nach "CRM Craft", aber der korrekte Name ist CRM Kraft. Hier erfährst du, warum wir so heißen und wie wir die Kraft in dein CRM-System bringen.',
        ctaDemo: 'Kostenlose Demo buchen',
        ctaWebsite: 'Zu CRM Kraft'
      },
      explanation: {
        title: 'Warum CRM Kraft und nicht CRM Craft?',
        intro: 'Der Name ist Programm: Wir bringen die Kraft (Power) in dein CRM-System.',
        points: [{
          title: 'CRM = Customer Relationship Management',
          description: 'Das Fundament für erfolgreichen Vertrieb'
        }, {
          title: 'Kraft = Power, Stärke, Energie',
          description: 'Die transformative Energie, die wir in dein System bringen'
        }, {
          title: 'CRM Kraft = Leistungsstarkes CRM',
          description: 'Ein CRM-System, das wirklich arbeitet und Umsatz generiert'
        }]
      },
      whatWeDo: {
        title: 'Was ist CRM Kraft?',
        subtitle: 'Wir sind kein CRM-System - wir sind dein CRM-Erfolgspartner',
        description: 'CRM Kraft ist ein spezialisierter CRM-Beratungs- und Implementierungsservice. Wir arbeiten hauptsächlich mit Close CRM und helfen Unternehmen, ihre CRM-Systeme optimal zu nutzen.',
        services: ['CRM-Strategie & Beratung', 'Close CRM Implementierung', 'KI-gestützte Automatisierung', 'Vertriebsprozess-Optimierung', 'Team Training & Support', 'Laufende Optimierung']
      },
      comparison: {
        title: 'CRM Kraft vs. Andere CRM-Anbieter',
        subtitle: 'Der Unterschied liegt in unserem Ansatz',
        wrong: {
          title: 'Typische CRM-Anbieter',
          points: ['Verkaufen nur Software-Lizenzen', 'Keine individuelle Beratung', 'Standard-Setup ohne Anpassung', 'Kein laufender Support', 'Du bist auf dich allein gestellt']
        },
        right: {
          title: 'CRM Kraft Ansatz',
          points: ['Ganzheitliche CRM-Strategie', 'Individuelle Beratung & Analyse', 'Maßgeschneidertes Setup', 'Laufende Optimierung & Support', 'Persönlicher Erfolgspartner']
        }
      },
      cta: {
        title: 'Bereit für mehr Kraft in deinem CRM?',
        subtitle: 'Lass uns in einer kostenlosen Demo zeigen, wie CRM Kraft dein Vertriebsteam transformiert',
        button: 'Kostenlose Demo buchen'
      },
      stats: {
        title: 'CRM Kraft in Zahlen',
        items: [{
          number: '110+',
          label: 'Implementierte CRM-Systeme'
        }, {
          number: '€7M+',
          label: 'Zusätzlicher Umsatz generiert'
        }, {
          number: '1.200+',
          label: 'Vertriebsprofis betreut'
        }, {
          number: '100%',
          label: 'Close CRM Partner'
        }]
      }
    },
    en: {
      hero: {
        title: 'CRM Kraft or CRM Craft?',
        subtitle: "The Answer: It's CRM Kraft",
        description: 'Many search for "CRM Craft", but the correct name is CRM Kraft. Here\'s why we\'re called that and how we bring power to your CRM system.',
        ctaDemo: 'Book Free Demo',
        ctaWebsite: 'Go to CRM Kraft'
      },
      explanation: {
        title: 'Why CRM Kraft and not CRM Craft?',
        intro: 'The name says it all: We bring the power (Kraft) into your CRM system.',
        points: [{
          title: 'CRM = Customer Relationship Management',
          description: 'The foundation for successful sales'
        }, {
          title: 'Kraft = Power, Strength, Energy',
          description: 'The transformative energy we bring to your system'
        }, {
          title: 'CRM Kraft = Powerful CRM',
          description: 'A CRM system that actually works and generates revenue'
        }]
      },
      whatWeDo: {
        title: 'What is CRM Kraft?',
        subtitle: "We're not a CRM system - we're your CRM success partner",
        description: 'CRM Kraft is a specialized CRM consulting and implementation service. We mainly work with Close CRM and help companies optimally use their CRM systems.',
        services: ['CRM Strategy & Consulting', 'Close CRM Implementation', 'AI-powered Automation', 'Sales Process Optimization', 'Team Training & Support', 'Ongoing Optimization']
      },
      comparison: {
        title: 'CRM Kraft vs. Other CRM Providers',
        subtitle: 'The difference lies in our approach',
        wrong: {
          title: 'Typical CRM Providers',
          points: ['Only sell software licenses', 'No individual consulting', 'Standard setup without customization', 'No ongoing support', "You're on your own"]
        },
        right: {
          title: 'CRM Kraft Approach',
          points: ['Holistic CRM strategy', 'Individual consulting & analysis', 'Tailored setup', 'Ongoing optimization & support', 'Personal success partner']
        }
      },
      cta: {
        title: 'Ready for More Power in Your CRM?',
        subtitle: 'Let us show you in a free demo how CRM Kraft transforms your sales team',
        button: 'Book Free Demo'
      },
      stats: {
        title: 'CRM Kraft in Numbers',
        items: [{
          number: '110+',
          label: 'Implemented CRM Systems'
        }, {
          number: '€7M+',
          label: 'Additional Revenue Generated'
        }, {
          number: '1,200+',
          label: 'Sales Professionals Supported'
        }, {
          number: '100%',
          label: 'Close CRM Partner'
        }]
      }
    }
  };
  const t = content[language];
  return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-649">
      <SEO title={language === 'de' ? 'CRM Kraft oder Kraft CRM? | Offizieller Close CRM Partner' : 'CRM Kraft or Kraft CRM? | Official Close CRM Partner'} description={language === 'de' ? 'CRM Kraft (nicht Kraft CRM) - Offizieller Close CRM Partner. Über 110 implementierte CRM-Systeme, €7M+ zusätzlicher Umsatz, 1.200+ betreute Vertriebsprofis. Kostenlose Demo buchen.' : 'CRM Kraft (not Kraft CRM) - Official Close CRM Partner. 110+ implemented CRM systems, €7M+ additional revenue, 1,200+ sales professionals supported. Book free demo.'} canonical="https://kraftcrm.de/" ogImage="https://kraftcrm.de/og-image-kraft-crm.jpg" data-id="element-650" />
      <Navigation data-id="element-651" />

      <main className="pt-16 lg:pt-20" data-id="element-652">
        {/* Hero Section */}
        <section className="px-4 py-16 bg-gradient-to-br from-[#1a1f36] via-[#0a0e1a] to-[#0a0e1a]" data-id="element-653">
          <div className="max-w-4xl mx-auto text-center" data-id="element-654">
            <div className="inline-flex items-center gap-2 bg-[#ff6b35]/10 text-[#ff6b35] px-4 py-2 rounded-full mb-6" data-id="element-655">
              <SparklesIcon className="w-4 h-4" aria-hidden="true" data-id="element-656" />
              <span className="text-sm font-semibold" data-id="element-657">
                {language === 'de' ? 'Häufig gesucht' : 'Frequently Searched'}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-658">
              {t.hero.title}
            </h1>
            <p className="text-2xl sm:text-3xl text-[#ff6b35] font-bold mb-6" data-id="element-659">
              {t.hero.subtitle}
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8" data-id="element-660">
              {t.hero.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-661">
              <button onClick={openCalendlyPopup} className="inline-flex items-center gap-2 bg-[#ff6b35] hover:bg-[#ff5722] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors w-full sm:w-auto" aria-label={language === 'de' ? 'Kostenlose Demo buchen' : 'Book free demo'} data-id="element-662">
                <CalendarIcon className="w-5 h-5" aria-hidden="true" data-id="element-663" />
                {t.hero.ctaDemo}
              </button>
              <a href="https://crm-kraft.de/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors w-full sm:w-auto" aria-label={language === 'de' ? 'Zur CRM Kraft Hauptseite' : 'Go to CRM Kraft main site'} data-id="element-664">
                {t.hero.ctaWebsite}
                <ExternalLinkIcon className="w-5 h-5" aria-hidden="true" data-id="element-665" />
              </a>
            </div>
          </div>
        </section>

        {/* Explanation Section */}
        <section className="px-4 py-16 bg-[#0d1225]" aria-labelledby="explanation-heading" data-id="element-666">
          <div className="max-w-4xl mx-auto" data-id="element-667">
            <h2 id="explanation-heading" className="text-3xl sm:text-4xl font-bold text-white text-center mb-4" data-id="element-668">
              {t.explanation.title}
            </h2>
            <p className="text-xl text-gray-300 text-center mb-12" data-id="element-669">
              {t.explanation.intro}
            </p>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-670">
              {t.explanation.points.map((point, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10 text-center" data-id="element-671">
                  <div className="text-4xl font-bold text-[#ff6b35] mb-3" data-id="element-672">
                    {index === 0 ? 'CRM' : index === 1 ? 'Kraft' : 'CRM Kraft'}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-673">
                    {point.title}
                  </h3>
                  <p className="text-gray-400 text-sm" data-id="element-674">{point.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="px-4 py-16 bg-[#0a0e1a]" data-id="element-675">
          <div className="max-w-6xl mx-auto" data-id="element-676">
            <div className="text-center mb-12" data-id="element-677">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-678">
                {t.whatWeDo.title}
              </h2>
              <p className="text-xl text-[#ff6b35] font-semibold mb-4" data-id="element-679">
                {t.whatWeDo.subtitle}
              </p>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto" data-id="element-680">
                {t.whatWeDo.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-681">
              {t.whatWeDo.services.map((service, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10 flex items-center gap-3" data-id="element-682">
                  <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" data-id="element-683" />
                  <span className="text-white font-medium" data-id="element-684">{service}</span>
                </div>)}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="px-4 py-16 bg-[#0d1225]" data-id="element-685">
          <div className="max-w-6xl mx-auto" data-id="element-686">
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12" data-id="element-687">
              {t.stats.title}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-688">
              {t.stats.items.map((stat, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-8 rounded-xl border border-white/10 text-center" data-id="element-689">
                  <div className="text-4xl sm:text-5xl font-bold text-[#ff6b35] mb-2" data-id="element-690">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 font-medium" data-id="element-691">{stat.label}</div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="px-4 py-16 bg-[#0a0e1a]" data-id="element-692">
          <div className="max-w-6xl mx-auto" data-id="element-693">
            <div className="text-center mb-12" data-id="element-694">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-695">
                {t.comparison.title}
              </h2>
              <p className="text-xl text-gray-300" data-id="element-696">{t.comparison.subtitle}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8" data-id="element-697">
              {/* Wrong Approach */}
              <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 p-8 rounded-xl border border-red-500/30" data-id="element-698">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-699">
                  <XCircleIcon className="w-8 h-8 text-red-400" data-id="element-700" />
                  {t.comparison.wrong.title}
                </h3>
                <ul className="space-y-4" data-id="element-701">
                  {t.comparison.wrong.points.map((point, index) => <li key={index} className="flex items-start gap-3" data-id="element-702">
                      <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-703" />
                      <span className="text-gray-300" data-id="element-704">{point}</span>
                    </li>)}
                </ul>
              </div>

              {/* Right Approach */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 p-8 rounded-xl border border-green-500/30" data-id="element-705">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-706">
                  <CheckCircleIcon className="w-8 h-8 text-green-400" data-id="element-707" />
                  {t.comparison.right.title}
                </h3>
                <ul className="space-y-4" data-id="element-708">
                  {t.comparison.right.points.map((point, index) => <li key={index} className="flex items-start gap-3" data-id="element-709">
                      <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-710" />
                      <span className="text-gray-300" data-id="element-711">{point}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 py-16 bg-gradient-to-br from-[#1a1f36] via-[#0a0e1a] to-[#0a0e1a]" data-id="element-712">
          <div className="max-w-4xl mx-auto text-center" data-id="element-713">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-714">
              {t.cta.title}
            </h2>
            <p className="text-xl text-gray-300 mb-8" data-id="element-715">{t.cta.subtitle}</p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-716">
              <button onClick={openCalendlyPopup} className="inline-flex items-center gap-2 bg-[#ff6b35] hover:bg-[#ff5722] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors w-full sm:w-auto" data-id="element-717">
                <CalendarIcon className="w-5 h-5" data-id="element-718" />
                {t.cta.button}
              </button>
              <a href="https://crm-kraft.de/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors w-full sm:w-auto" data-id="element-719">
                {t.hero.ctaWebsite}
                <ExternalLinkIcon className="w-5 h-5" data-id="element-720" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-721" />
    </div>;
}